/**
 * Editable LLM Prompts API.
 *
 * Backed by the issue_analysis_prompts Postgres table. Edits are
 * picked up by the backend cache immediately on PUT (the backend
 * invalidates its 30 s TTL cache after a successful update).
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

export interface PromptRow {
  prompt_key: string;
  prompt_text: string;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
  default_text: string;
}

export interface PromptHistoryRow {
  history_id: number;
  prompt_key: string;
  prompt_text: string;
  updated_at: string;
  updated_by: string | null;
  archived_at: string;
}

class PromptsApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/prompts`;
  }

  async list(): Promise<PromptRow[]> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(this.baseUrl, { headers });
    if (!response.ok) {
      throw new Error(`Failed to list prompts: ${response.statusText}`);
    }
    return response.json();
  }

  async update(
    key: string,
    promptText: string,
    updatedBy?: string,
  ): Promise<PromptRow> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ prompt_text: promptText, updated_by: updatedBy }),
    });
    if (!response.ok) {
      throw new Error(`Failed to update prompt: ${response.statusText}`);
    }
    return response.json();
  }

  async reset(key: string): Promise<PromptRow> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${this.baseUrl}/${encodeURIComponent(key)}/reset`,
      { method: 'POST', headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to reset prompt: ${response.statusText}`);
    }
    return response.json();
  }

  async history(key: string): Promise<PromptHistoryRow[]> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${this.baseUrl}/${encodeURIComponent(key)}/history`,
      { headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to load prompt history: ${response.statusText}`);
    }
    return response.json();
  }

  async rollback(key: string, historyId: number): Promise<PromptRow> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${this.baseUrl}/${encodeURIComponent(key)}/rollback/${historyId}`,
      { method: 'POST', headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to roll back prompt: ${response.statusText}`);
    }
    return response.json();
  }
}

export const promptsApi = new PromptsApi();
